import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Zap, CheckCircle2, Smartphone, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ActivateBooklet() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/booklet/activation").then(({ data }) => setInfo(data)).catch(() => setInfo({ phone: user?.phone }));
  }, [user]);

  const activate = async () => {
    if (code.trim().length < 3) { toast.error("Enter the booklet code printed on your cover."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/booklet/checkout", { code: code.trim(), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const phone = info?.phone || user?.phone;

  if (info === null) {
    return <div className="flex justify-center py-24" data-testid="activate-loading"><Loader2 className="animate-spin text-race-red" size={40} /></div>;
  }

  return (
    <div className="max-w-xl mx-auto animate-slide-up space-y-8" data-testid="activate-booklet-page">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <BookOpen className="text-race-red" /> ACTIVATE BOOKLET
        </h1>
        <p className="text-race-sub text-sm mt-2">
          Activate your Text Racing Edutainment booklet to unlock <span className="text-race-txt font-semibold">$15.00 in race credits</span>.
        </p>
      </div>

      {info?.activated ? (
        <div className="bg-race-surface border border-green-500/40 p-8 text-center" data-testid="activate-already">
          <CheckCircle2 className="mx-auto text-green-400" size={48} />
          <h2 className="font-heading text-2xl tracking-tight mt-3">BOOKLET ACTIVATED</h2>
          <p className="text-race-sub text-sm mt-2">
            Booklet <span className="text-race-txt font-mono">{info.code}</span> is bound to your phone. Only one booklet can be activated per phone.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/buy-credits" data-testid="activate-buy-more" className="border border-race-line hover:border-race-red text-race-txt font-mono text-xs uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">Buy more credits</Link>
            <Link to="/dashboard" data-testid="activate-to-dashboard" className="bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">Go race</Link>
          </div>
        </div>
      ) : (
        <div className="bg-race-surface border border-race-line p-6 sm:p-8 space-y-6" data-testid="activate-form">
          {/* Offer */}
          <div className="flex items-center gap-5 pb-6 border-b border-race-line">
            <div className="text-center shrink-0">
              <div className="font-heading text-5xl leading-none">$12.99</div>
              <div className="overline mt-1">to activate</div>
            </div>
            <div className="h-12 w-px bg-race-line" />
            <p className="text-sm text-race-txt leading-snug">
              Includes <span className="font-bold text-green-400">$15.00 in race credits</span> and binds this booklet to your phone. One booklet per phone.
            </p>
          </div>

          {/* Phone confirm */}
          <div>
            <div className="overline mb-2 flex items-center gap-2"><Smartphone size={13} /> Assigned to phone</div>
            <div className="bg-race-bg border border-race-line rounded-sm px-4 py-3 font-mono text-race-txt" data-testid="activate-phone">
              {phone || <span className="text-race-muted">No phone on file - add one in your profile first.</span>}
            </div>
          </div>

          {/* Code entry */}
          <div>
            <label className="overline mb-2 block" htmlFor="booklet-code">Booklet code</label>
            <input id="booklet-code" data-testid="activate-code-input" value={code}
              onChange={(e) => setCode(e.target.value)} placeholder="e.g. #0040001"
              className="w-full bg-race-bg border border-race-line focus:border-race-red rounded-sm px-4 py-3 font-mono text-race-txt outline-none transition-colors" />
            <p className="text-race-muted text-xs mt-2">Find the code printed on your booklet cover. Each code activates once.</p>
          </div>

          <button onClick={activate} disabled={busy || !phone} data-testid="activate-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-mono text-sm uppercase tracking-wide py-4 rounded-sm transition-colors">
            <Zap size={16} /> {busy ? "Redirecting to checkout…" : "Activate for $12.99"}
          </button>
          <p className="text-race-muted text-xs text-center">Secure checkout by Stripe. Test mode - use card 4242 4242 4242 4242, any future expiry & CVC.</p>
        </div>
      )}
    </div>
  );
}
